/** @format */

import { styled, keyframes } from "@mui/material/styles";
import { Typography, Box } from "@mui/material";
import { colors } from "../../utility/types/constant";

// Animations
const floatRandom = keyframes`
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  33% { transform: translate(26px, -28px) rotate(120deg); }
  66% { transform: translate(-18px, 18px) rotate(240deg); }
`;

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const badgePulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(30, 193, 149, 0.65);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 12px rgba(30, 193, 149, 0);
  }
`;

const cardFloat = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 18px rgba(30, 193, 149, 0.35); }
  50% { box-shadow: 0 0 36px rgba(30, 193, 149, 0.7); }
`;

export const Container = styled("section")(({ theme }) => ({
	position: "relative",
	padding: "80px 20px",
	marginTop: "60px",
	marginBottom: "60px",
	overflow: "hidden",

	/**
	 * Glassmorphism parent background:
	 * - Uses a subtle gradient + translucent layer
	 * - Backdrop blur so the section looks like frosted glass over the page
	 */
	background: `linear-gradient(135deg, ${colors.primary} 0%, #051442 50%, ${colors.thirdPrimary} 100%)`,
	backdropFilter: "blur(16px)",
	borderTop: "1px solid rgba(255, 255, 255, 0.12)",
	borderBottom: "1px solid rgba(255, 255, 255, 0.10)",

	// Optional subtle inner glow
	boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",

	[theme.breakpoints.down("sm")]: {
		padding: "60px 15px",
	},
}));

export const GradientOverlay = styled("div")({
	position: "absolute",
	inset: 0,
	background: `
    radial-gradient(circle at 18% 38%, rgba(30, 193, 149, 0.14) 0%, transparent 55%),
    radial-gradient(circle at 85% 75%, rgba(30, 193, 149, 0.12) 0%, transparent 55%),
    radial-gradient(circle at 60% 10%, rgba(255, 255, 255, 0.06) 0%, transparent 55%)
  `,
	pointerEvents: "none",
	zIndex: 1,
});

export const FloatingElement = styled(Box)(({ theme }) => ({
	position: "absolute",
	width: "110px",
	height: "110px",
	borderRadius: "50%",
	background: `radial-gradient(circle, rgba(30, 193, 149, 0.22), transparent 70%)`,
	filter: "blur(0px)",
	animation: `${floatRandom} 20s ease-in-out infinite`,
	zIndex: 1,

	[theme.breakpoints.down("sm")]: {
		width: "70px",
		height: "70px",
	},
}));

export const HeaderDiv = styled("div")(({ theme }) => ({
	textAlign: "center",
	marginBottom: "34px",
	position: "relative",
	zIndex: 2,
}));

export const AnimatedBadge = styled("div")(({ theme }) => ({
	display: "inline-block",
	padding: "10px 28px",
	backgroundColor: colors.mainSecondary,
	color: "#ffffff",
	borderRadius: "50px",
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "0.875rem",
	fontWeight: 800,
	textTransform: "uppercase",
	letterSpacing: "1px",
	marginBottom: "18px",
	animation: `${badgePulse} 2s ease-in-out infinite`,
	boxShadow: "0 4px 18px rgba(30, 193, 149, 0.35)",

	[theme.breakpoints.down("sm")]: {
		fontSize: "0.75rem",
		padding: "8px 20px",
	},
}));

export const MainTitle = styled(Typography)(({ theme }) => ({
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "clamp(2rem, 4vw, 3.25rem)",
	fontWeight: 900,
	color: "#ffffff",
	marginBottom: "16px",
	lineHeight: 1.15,
	letterSpacing: "-0.02em",

	[theme.breakpoints.down("sm")]: {
		fontSize: "clamp(1.75rem, 5vw, 2.4rem)",
	},
}));

export const HighlightText = styled("span")({
	background: `linear-gradient(135deg, ${colors.mainSecondary} 0%, #26e3b5 100%)`,
	WebkitBackgroundClip: "text",
	WebkitTextFillColor: "transparent",
	backgroundClip: "text",
	display: "inline-block",
	animation: `${shimmer} 3s ease-in-out infinite`,
	backgroundSize: "200% auto",
});

export const SectionSubtitle = styled(Typography)(({ theme }) => ({
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "clamp(1rem, 2vw, 1.2rem)",
	fontWeight: 400,
	color: "rgba(255, 255, 255, 0.85)",
	maxWidth: "900px",
	margin: "0 auto",
	lineHeight: 1.8,

	[theme.breakpoints.down("sm")]: {
		fontSize: "clamp(0.95rem, 2vw, 1.1rem)",
	},
}));

export const ContentWrapper = styled("div")({
	position: "relative",
	zIndex: 2,
});

export const FeatureCard = styled("div")(({ theme }) => ({
	backgroundColor: "rgba(255, 255, 255, 0.06)",
	backdropFilter: "blur(12px)",
	borderRadius: "22px",
	padding: "34px 26px",
	textAlign: "left",
	border: "1px solid rgba(255, 255, 255, 0.12)",
	position: "relative",
	overflow: "hidden",
	transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
	animation: `${cardFloat} 7s ease-in-out infinite`,

	"&::before": {
		content: '""',
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		height: "3px",
		background: `linear-gradient(90deg, transparent, ${colors.mainSecondary}, transparent)`,
		transform: "translateX(-100%)",
		transition: "transform 0.7s ease",
	},

	"&:hover": {
		transform: "translateY(-10px)",
		backgroundColor: "rgba(255, 255, 255, 0.09)",
		border: `1px solid rgba(30, 193, 149, 0.55)`,
		boxShadow: "0 18px 44px rgba(0, 0, 0, 0.35)",

		"&::before": {
			transform: "translateX(100%)",
		},
	},

	[theme.breakpoints.down("sm")]: {
		padding: "28px 20px",
	},
}));

export const IconCircle = styled("div")(({ theme }) => ({
	width: "78px",
	height: "78px",
	borderRadius: "18px",
	background: `linear-gradient(135deg, rgba(30, 193, 149, 0.18) 0%, rgba(38, 227, 181, 0.14) 100%)`,
	border: `1px solid rgba(30, 193, 149, 0.65)`,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	marginBottom: "18px",
	transition: "all 0.3s ease",
	animation: `${glow} 3s ease-in-out infinite`,

	[theme.breakpoints.down("sm")]: {
		width: "70px",
		height: "70px",
	},
}));

export const IconWrapper = styled("div")({
	color: colors.mainSecondary,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	"& svg": {
		fontSize: "40px",
	},
});

export const FeatureTitle = styled(Typography)(({ theme }) => ({
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "clamp(1.1rem, 1.4vw, 1.3rem)",
	fontWeight: 800,
	color: "#ffffff",
	marginBottom: "10px",
	letterSpacing: "0.2px",
}));

export const FeatureDescription = styled(Typography)(({ theme }) => ({
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "clamp(0.95rem, 1.15vw, 1rem)",
	fontWeight: 400,
	color: "rgba(255, 255, 255, 0.78)",
	lineHeight: 1.7,
}));

export const CTAWrapper = styled("div")(({ theme }) => ({
	display: "flex",
	gap: "14px",
	justifyContent: "center",
	alignItems: "center",
	marginTop: "22px",
	flexWrap: "wrap",
}));

export const PrimaryCTA = styled("a")(({ theme }) => ({
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
	padding: "12px 20px",
	borderRadius: "14px",
	textDecoration: "none",
	fontFamily: "'Montserrat', sans-serif",
	fontWeight: 800,
	letterSpacing: "0.2px",
	color: "#041245",
	background: `linear-gradient(135deg, ${colors.mainSecondary} 0%, #26e3b5 100%)`,
	boxShadow: "0 10px 28px rgba(30, 193, 149, 0.28)",
	transition: "all 0.25s ease",

	"&:hover": {
		transform: "translateY(-2px)",
		boxShadow: "0 14px 34px rgba(30, 193, 149, 0.38)",
	},
}));

export const SecondaryCTA = styled("a")(({ theme }) => ({
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
	padding: "12px 20px",
	borderRadius: "14px",
	textDecoration: "none",
	fontFamily: "'Montserrat', sans-serif",
	fontWeight: 800,
	letterSpacing: "0.2px",
	color: "#ffffff",
	backgroundColor: "rgba(255, 255, 255, 0.06)",
	border: "1px solid rgba(255, 255, 255, 0.14)",
	backdropFilter: "blur(10px)",
	transition: "all 0.25s ease",

	"&:hover": {
		transform: "translateY(-2px)",
		backgroundColor: "rgba(255, 255, 255, 0.09)",
		border: `1px solid rgba(30, 193, 149, 0.55)`,
	},
}));

export const BottomNote = styled(Typography)(({ theme }) => ({
	marginTop: "16px",
	textAlign: "center",
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "0.9rem",
	color: "rgba(255, 255, 255, 0.70)",
	lineHeight: 1.6,
	maxWidth: "980px",
	marginLeft: "auto",
	marginRight: "auto",

	[theme.breakpoints.down("sm")]: {
		fontSize: "0.85rem",
	},
}));
